import {
  LoaderCircle,
  Ban,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from '../abastecimiento/ModalBase'
import { anularPagoCompra } from '../../services/cuentasPorPagar'
import {
  etiquetaMedioPago,
  MOTIVO_ANULACION_MAX,
  validarMotivoAnulacion,
} from '../../utils/cuentasPorPagar'
import {
  formatearFechaPos,
  formatearMontoPos,
} from '../../utils/pos'

// Confirmación explícita antes de anular (solo administradores; el motivo
// es obligatorio, de 5 a 500 caracteres). El pago anulado deja de sumar.
function AnularPagoModal({
  pago,
  empresaId,
  onCerrar,
  onAnulado,
}) {
  const [motivo, setMotivo] = useState('')
  const [mostrarError, setMostrarError] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState('')
  const enCursoRef = useRef(false)

  useEffect(() => {
    setMotivo('')
    setMostrarError(false)
    setErrorEnvio('')
    enCursoRef.current = false
  }, [pago?.id])

  if (!pago) return null

  const errorMotivo = validarMotivoAnulacion(motivo)

  const confirmar = async (event) => {
    event.preventDefault()

    if (enCursoRef.current) return

    setMostrarError(true)

    if (errorMotivo) return

    enCursoRef.current = true
    setEnviando(true)
    setErrorEnvio('')

    try {
      await anularPagoCompra({
        empresaId,
        pagoId: pago.id,
        motivo,
      })

      onAnulado?.()
    } catch (errorAnular) {
      setErrorEnvio(
        errorAnular.message || 'No fue posible anular el pago.'
      )
      enCursoRef.current = false
    } finally {
      setEnviando(false)
    }
  }

  return (
    <ModalBase
      abierto
      titulo="Anular pago"
      tituloId="titulo-anular-pago"
      anchoMaximo="max-w-lg"
      procesando={enviando}
      onCerrar={onCerrar}
    >
      <form
        onSubmit={confirmar}
        noValidate
        className="mt-5 space-y-4"
      >
        <AvisoModal tipo="aviso">
          ¿Anular el pago de {formatearMontoPos(pago.monto)} del{' '}
          {formatearFechaPos(pago.fecha_pago)} (
          {etiquetaMedioPago(pago.medio_pago)})? Seguirá en el historial,
          pero dejará de sumar al total abonado y el saldo se recalculará.
        </AvisoModal>

        <CampoModal
          label="Motivo de la anulación"
          htmlFor="anular-motivo"
          requerido
        >
          <textarea
            id="anular-motivo"
            rows={3}
            maxLength={MOTIVO_ANULACION_MAX}
            value={motivo}
            disabled={enviando}
            onChange={(event) => {
              setMotivo(event.target.value)
              setErrorEnvio('')
            }}
            className={estiloInputModal}
          />
          {mostrarError && errorMotivo && (
            <p className="mt-1 text-sm text-red-600">{errorMotivo}</p>
          )}
        </CampoModal>

        {errorEnvio && <AvisoModal tipo="error">{errorEnvio}</AvisoModal>}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="min-h-12 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={enviando}
            className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-3 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enviando ? (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            ) : (
              <Ban size={18} />
            )}
            Anular pago
          </button>
        </div>
      </form>
    </ModalBase>
  )
}

export default AnularPagoModal
